import type { MouseEvent } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { ProductKeys } from "@atlassiansox/asc-stargate";

import { ConfluenceTemplateMessages } from "@/components/confluence/templates/template-messages";
import { useSiteSelectorContext } from "@/components/site-selector-modal/site-selector-context";
import { wacToken } from "@/libraries/design/foundations";
import { useGupsStatus } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/hooks/use-gups";

const templateButtonActionLinkStyles = css({
    "alignItems": "center",
    "justifyContent": "center",
    "background": `${wacToken("color.extended.blue700")}`,
    "borderRadius": "0.25rem",
    "boxSizing": "border-box",
    "display": "inline-flex",
    "minWidth": 128,
    "margin": 0,
    "height": "auto",
    "minHeight": 32,
    "border": "none",
    "lineHeight": "1.2rem",
    "textAlign": "center",
    "cursor": "pointer",
    "padding": "0 16px",
    "fontSize": ".875rem",
    "fontWeight": 500,
    "fontFamily": "var(--font-family-charlie-text)",
    "color": "#ffffff",
    "textDecoration": "none",
    "zIndex": 1,
    ":hover": {
        background: `${wacToken("color.extended.blue800")}`
    },
    ":focus": {
        background: `${wacToken("color.extended.blue800")}`,
        outlineOffset: "4px"
    },
    ":active": {
        background: `${wacToken("color.extended.blue800")}`
    }
});

interface TemplateCardProps {
    blueprintModuleKey?: string | null;
    templateName: string;
}

export const UseTemplateButton = ({
    blueprintModuleKey,
    templateName
}: TemplateCardProps) => {
    const intl = useIntl();
    const { openModalForTemplate } = useSiteSelectorContext();
    const { addTemplateCardCallback } = useGupsStatus();

    const navigateToUseTemplate = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        event.preventDefault();

        const templateId = blueprintModuleKey ?? "";
        openModalForTemplate({
            templateId,
            templateName,
            productKey: ProductKeys.CONFLUENCE
        });
        addTemplateCardCallback(templateId);
    };

    const buttonTemplateId = templateName.replace(/ /g, "-");
    const buttonId = `use-template-button-${buttonTemplateId}`;

    return (
        <button
            css={templateButtonActionLinkStyles}
            onClick={navigateToUseTemplate}
            className="doNotAutoTrack"
            aria-label={intl.formatMessage(
                ConfluenceTemplateMessages.useTemplateButtonAriaText,
                { template: templateName }
            )}
            id={buttonId}
        >
            <FormattedMessage {...ConfluenceTemplateMessages.useTemplate} />
        </button>
    );
};
